export function calendarDay(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value < '0001-01-01') throw new Error('Use a valid calendar date.');
  const time = Date.parse(`${value}T00:00:00Z`);
  if (!Number.isFinite(time) || new Date(time).toISOString().slice(0,10) !== value) throw new Error('Use a valid calendar date.');
  return time;
}

export function inclusiveRange(start, end) {
  calendarDay(start);
  const last = calendarDay(end);
  if (start > end || end === '9999-12-31') throw new Error('Choose a valid date range.');
  return {start, end_exclusive: new Date(last + 86400000).toISOString().slice(0,10)};
}

export function inclusiveEnd(range) {
  return range ? new Date(calendarDay(range.end_exclusive) - 86400000).toISOString().slice(0,10) : '';
}

// params: the board's effective ReportParams (normalParams), saved or the viewer's unsaved draft. A definition
// panel reads with all of them; a dated source panel only with the period, which the server prefers to the range.
export function panelReadIdentity(panel, dashboardRange, params = null) {
  if (panel.definition) return JSON.stringify([panel.id,null,panel.available,panel.definition,params]);
  const range = panel.source?.parameter_mode === 'date_range_v1' ? params?.period ?? (dashboardRange || panel.source.default_range) : null;
  return JSON.stringify([panel.id,panel.source_id,panel.available,range]);
}

// ReportParams in one canonical shape (app/bi/definition.py): a preset carries no dates, choices sort by name.
export function normalParams(p) {
  const period = !p?.period ? null : p.period.preset === 'custom'
    ? {preset:'custom',start:p.period.start,end_exclusive:p.period.end_exclusive} : {preset:p.period.preset};
  const choices = Object.fromEntries(Object.keys(p?.choices || {}).sort().map(name => {
    const c = p.choices[name];
    return [name,{dataset:c.dataset,field:c.field,value:c.value ?? null}];
  }));
  return {period,choices,compare:!!p?.compare};
}
export const paramsEqual = (a, b) => JSON.stringify(normalParams(a)) === JSON.stringify(normalParams(b));
export const isReport = definition => !!definition?.panels.some(p => p.definition);
const effective = (definition, draft) => draft ?? normalParams(definition?.params);
export const viewerParams = state => effective(state.definition, state.draft);
// The one refresh-board body for what the viewer sees; Save is the only thing that writes params.
export const boardRequest = state => ({expected_revision:state.definition.revision,params:viewerParams(state)});
export const saveOperations = state => state.draft ? [{op:'set_params',params:state.draft}] : [];
export const validReport = reply => !!reply && reply.source_id === null && !!reply.result && Array.isArray(reply.result.columns)
  && Array.isArray(reply.result.rows) && typeof reply.prescription?.mark === 'string';

// ---- the period pane. today is 'YYYY-MM-DD' (the browser's day; the server resolves presets in tenant time) ----
const ym = day => ({y:Number(day.slice(0,4)),m:Number(day.slice(5,7))});
const monthIso = (y, m) => `${String(y).padStart(4,'0')}-${String(m).padStart(2,'0')}-01`;
const addMonths = (y, m, n) => {const t = y*12+m-1+n; return {y:Math.floor(t/12),m:t%12+1};};
export const RELATIVE = ['ytd','this_quarter','last_12_months'];
export function monthSpan(period, today) {
  if (!period) return null;
  const t = ym(today), q = Math.floor((t.m-1)/3)*3+1;
  if (period.preset === 'this_year') return {from:{y:t.y,m:1},to:{y:t.y,m:12}};
  if (period.preset === 'last_year') return {from:{y:t.y-1,m:1},to:{y:t.y-1,m:12}};
  if (period.preset === 'ytd') return {from:{y:t.y,m:1},to:t};
  if (period.preset === 'this_quarter') return {from:{y:t.y,m:q},to:{y:t.y,m:q+2}};
  if (period.preset === 'last_12_months') return {from:addMonths(t.y,t.m,-11),to:t};
  return {from:ym(period.start),to:ym(new Date(calendarDay(period.end_exclusive)-86400000).toISOString())};
}
// A whole current or last year is stored as its preset, so it still means "this year" next year.
export function spanPeriod(year, from, to, today) {
  const [a,b] = from <= to ? [from,to] : [to,from], t = ym(today);
  if (a === 1 && b === 12 && (year === t.y || year === t.y-1)) return {preset:year === t.y ? 'this_year' : 'last_year'};
  const end = addMonths(year,b,1);
  return {preset:'custom',start:monthIso(year,a),end_exclusive:monthIso(end.y,end.m)};
}
export function yearOf(period, today) {
  const s = monthSpan(period,today);
  return s && s.from.y === s.to.y ? s.from.y : null;
}
// The year a month or quarter choice lands in: the span's own year, or its last year when it crosses one.
const baseYear = (period, today) => monthSpan(period,today)?.to.y ?? ym(today).y;
export function withYear(period, year, today) {
  const s = monthSpan(period,today);
  return s && s.from.y === s.to.y ? spanPeriod(year,s.from.m,s.to.m,today) : spanPeriod(year,1,12,today);
}
export const withMonths = (period, from, to, today) => spanPeriod(baseYear(period,today),from,to,today);
export const QUICK = ['q1','q2','q3','q4','year',...RELATIVE];
export function quickPeriod(key, period, today) {
  if (RELATIVE.includes(key)) return {preset:key};
  const y = baseYear(period,today);
  if (key === 'year') return spanPeriod(y,1,12,today);
  const q = Number(key.slice(1));
  return spanPeriod(y,3*q-2,3*q,today);
}
export function quickOf(period, today) {
  if (!period) return null;
  if (RELATIVE.includes(period.preset)) return period.preset;
  if (period.preset === 'custom' && (period.start.slice(8) !== '01' || period.end_exclusive.slice(8) !== '01')) return null;
  const {from,to} = monthSpan(period,today);
  if (from.y !== to.y) return null;
  if (from.m === 1 && to.m === 12) return 'year';
  return (from.m-1)%3 === 0 && to.m === from.m+2 ? `q${(from.m+2)/3}` : null;
}
export const monthName = (m, lang, style = 'short') =>
  new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-US',{month:style,timeZone:'UTC'}).format(Date.UTC(2000,m-1,1));
const RELATIVE_WORDS = {ytd:['מתחילת השנה','Year to date'],this_quarter:['הרבעון הנוכחי','This quarter'],last_12_months:['12 החודשים האחרונים','Last 12 months']};
export function periodLabel(period, today, lang) {
  const he = lang === 'he';
  if (!period) return he ? 'כל התאריכים' : 'All dates';
  const s = monthSpan(period,today), quick = quickOf(period,today);
  const quarter = (y, q) => he ? `רבעון ${q} ${y}` : `Q${q} ${y}`;
  if (period.preset === 'this_year') return he ? `השנה · ${s.from.y}` : `This year · ${s.from.y}`;
  if (period.preset === 'last_year') return he ? `שנה שעברה · ${s.from.y}` : `Last year · ${s.from.y}`;
  if (period.preset === 'last_12_months') return RELATIVE_WORDS.last_12_months[he ? 0 : 1];
  if (period.preset === 'this_quarter') return `${RELATIVE_WORDS.this_quarter[he ? 0 : 1]} · ${quarter(s.from.y,(s.from.m+2)/3)}`;
  if (period.preset === 'ytd') return `${RELATIVE_WORDS.ytd[he ? 0 : 1]} · ${s.from.y}`;
  if (quick === 'year') return String(s.from.y);
  if (quick) return quarter(s.from.y,Number(quick.slice(1)));
  if (period.start.slice(8) !== '01' || period.end_exclusive.slice(8) !== '01') return `${period.start} – ${inclusiveEnd(period)}`;
  if (s.from.y !== s.to.y) return `${monthName(s.from.m,lang)} ${s.from.y} – ${monthName(s.to.m,lang)} ${s.to.y}`;
  return s.from.m === s.to.m ? `${monthName(s.from.m,lang,'long')} ${s.from.y}` : `${monthName(s.from.m,lang)}–${monthName(s.to.m,lang)} ${s.from.y}`;
}
export function compareLabel(period, today, lang) {
  const y = yearOf(period,today);
  return lang === 'he' ? y ? `מול ${y-1}` : 'מול אשתקד' : y ? `vs ${y-1}` : 'vs last year';
}

// A KPI tile is one grid row, as in the comp; every other visual needs two (app/data_dashboards/service.py _layout).
export const minRows = panel => (panel.definition?.kind === 'kpi' ? 1 : 2);
export function completeLayout(panels) {
  const layout = panels.map(panel => ({panel_id:panel.id,...panel.layout}));
  if (layout.length > 12 || new Set(layout.map(p=>p.panel_id)).size !== layout.length) throw new Error('Invalid panel layout.');
  for (const [index,p] of layout.entries()) {
    if (![p.x,p.y,p.w,p.h].every(Number.isInteger) || p.x < 0 || p.x > 11 || p.y < 0 || p.y > 1000 || p.w < 1 || p.w > 12 || p.h < minRows(panels[index]) || p.h > 12 || p.x+p.w > 12) throw new Error('Invalid panel layout.');
    if (layout.slice(0,index).some(q=>p.x<q.x+q.w && p.x+p.w>q.x && p.y<q.y+q.h && p.y+p.h>q.y)) throw new Error('Panels overlap.');
  }
  return layout;
}

const initial = {scopeKey:null,definition:null,draft:null,board:null,results:{},pending:{},errors:{},generation:0};
// Keep a result or an error only while its read identity still holds under the definition and params now shown.
function rebase(state, definition, draft) {
  const sameBoard = state.definition?.id === definition.id;
  const now = effective(definition,draft), before = sameBoard ? effective(state.definition,state.draft) : null;
  const results = {};
  const errors = {};
  for (const panel of definition.panels) {
    const identity = panelReadIdentity(panel,definition.range,now);
    const prior = state.results[panel.id];
    if (sameBoard && panel.available && prior?.identity === identity) results[panel.id] = prior;
    const previous = sameBoard && state.definition.panels.find(p=>p.id===panel.id);
    if (previous && panel.available && panelReadIdentity(previous,state.definition.range,before)===identity && state.errors[panel.id]) errors[panel.id]=state.errors[panel.id];
  }
  return {...state,definition,draft,board:null,results,pending:{},errors,generation:state.generation+1};
}
export function dashboardReducer(state = initial, event) {
  if (event.type === 'scope') return {...initial,scopeKey:event.scopeKey,generation:state.generation+1};
  if (event.type === 'invalidate') return {...state,board:null,results:Object.fromEntries(Object.entries(state.results).filter(([id])=>!event.panelIds.includes(id))),pending:{},errors:Object.fromEntries(Object.entries(state.errors).filter(([id])=>!event.panelIds.includes(id))),generation:state.generation+1};
  if (['clear','abort'].includes(event.type)) return {...state,board:null,results:{},pending:{},errors:{},generation:state.generation+1,...(event.type==='clear'?{definition:null,draft:null}:{})};
  if (event.type === 'metadata') {
    const definition = event.definition;
    // a reload of the same board keeps the viewer's unsaved params until they equal the saved ones
    const keep = state.definition?.id === definition.id && state.draft && !paramsEqual(state.draft,definition.params);
    return rebase(state,definition,keep ? state.draft : null);
  }
  if (!state.definition) return state;
  const params = effective(state.definition,state.draft);
  const identity = panel => panelReadIdentity(panel,state.definition.range,params);
  if (event.type === 'params') {
    const next = normalParams(event.params);
    return rebase(state,state.definition,paramsEqual(next,state.definition.params) ? null : next);
  }
  if (event.type === 'boardRead') {
    const generation = state.generation+1;
    const board = {scopeKey:state.scopeKey,boardId:state.definition.id,revision:state.definition.revision,generation,params:JSON.stringify(params)};
    const live = state.definition.panels.filter(p=>p.available);
    const drop = map => Object.fromEntries(Object.entries(map).filter(([id])=>!live.some(p=>p.id===id)));
    return {...state,generation,board,results:drop(state.results),errors:drop(state.errors),
      pending:Object.fromEntries(live.map(p=>[p.id,{board,identity:identity(p)}]))};
  }
  if (event.type === 'boardResult' || event.type === 'boardFailure') {
    const board = event.token;
    if (!board || state.board !== board || board.scopeKey !== state.scopeKey || board.boardId !== state.definition.id
      || board.revision !== state.definition.revision || board.params !== JSON.stringify(params)) return state;
    if (event.type === 'boardResult' && (event.reply?.dashboard_id !== board.boardId || event.reply?.revision !== board.revision)) return state;
    const pending = {...state.pending}, results = {...state.results}, errors = {...state.errors};
    const mine = id => pending[id]?.board === board && pending[id].identity === identity(state.definition.panels.find(p=>p.id===id));
    for (const entry of event.type === 'boardResult' ? event.entries : []) {
      if (!mine(entry.panelId)) continue;
      const token = pending[entry.panelId];
      delete pending[entry.panelId];
      if (entry.error) errors[entry.panelId] = entry.error;
      else results[entry.panelId] = entry.report ? {identity:token.identity,report:entry.report} : {identity:token.identity,artifact:entry.artifact};
    }
    // a panel the reply did not account for is a failure, never a spinner left forever
    for (const id of Object.keys(pending)) if (mine(id)) {delete pending[id];errors[id]=event.error;}
    return {...state,board:null,pending,results,errors};
  }
  const panel = state.definition.panels.find(p=>p.id===event.panelId);
  if (!panel) return state;
  if (event.type === 'read') {
    const generation = state.generation+1;
    const token = {scopeKey:state.scopeKey,boardId:state.definition.id,revision:state.definition.revision,generation,identity:identity(panel)};
    const results = {...state.results}; delete results[panel.id];
    const errors = {...state.errors}; delete errors[panel.id];
    return {...state,generation,results,errors,pending:{...state.pending,[panel.id]:token}};
  }
  if (!event.token || state.pending[panel.id] !== event.token || event.token.scopeKey !== state.scopeKey || event.token.revision !== state.definition.revision || event.token.identity !== identity(panel)) return state;
  const pending = {...state.pending}; delete pending[panel.id];
  if (event.type === 'result') return {...state,pending,results:{...state.results,[panel.id]:event.report ? {identity:event.token.identity,report:event.report} : {identity:event.token.identity,artifact:event.artifact}}};
  if (event.type === 'failure') return {...state,pending,errors:{...state.errors,[panel.id]:event.error}};
  return state;
}
